'use client';

import { useState } from 'react';
import { Link } from '@/components/LocaleLink';
import { useRouter } from 'next/navigation';
import { SlidersHorizontal, X, Check, Tag, Building2, ShieldCheck, Zap, Wallet } from 'lucide-react';
import { useLocale } from '@/hooks/useLocale';
import { t } from '@/i18n/t';

export interface Facet { id: number; name: string; slug?: string; count: number }
export interface SearchFacets {
  categories: Facet[];
  manufacturers: Facet[];
  price: { min: number; max: number } | null;
  total: number;
}

export interface FilterState {
  q?: string;
  ktype?: string;
  quality?: string;       // 'original' | 'aftermarket'
  express?: string;       // '1'
  category?: string;      // category id (search context)
  manufacturer?: string;  // /sok: part brand id · catalog: preserved vehicle-brand param
  brand?: string;         // catalog: part brand id
  minPrice?: string;
  maxPrice?: string;
}

/**
 * Shared faceted filter sidebar. Used on /sok (search) and catalog pages.
 * `basePath` + `brandKey` let it follow the SAME pattern in both places:
 *   - /sok:    basePath="/sok",  brandKey="manufacturer"
 *   - catalog: basePath="/katalog/<slug>", brandKey="brand" (vehicle `manufacturer` preserved)
 */
export function SearchFilters({
  facets,
  current,
  basePath = '/sok',
  brandKey = 'manufacturer',
  showCategories = true,
}: {
  facets: SearchFacets;
  current: FilterState;
  basePath?: string;
  brandKey?: 'manufacturer' | 'brand';
  showCategories?: boolean;
}) {
  const { locale } = useLocale();
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [minP, setMinP] = useState(current.minPrice ?? '');
  const [maxP, setMaxP] = useState(current.maxPrice ?? '');

  const url = (overrides: Partial<FilterState>): string => {
    const m = { ...current, ...overrides };
    const p = new URLSearchParams();
    if (m.q) p.set('q', m.q);
    if (m.ktype) p.set('ktype', m.ktype);
    if (m.quality) p.set('quality', m.quality);
    if (m.express) p.set('express', m.express);
    if (m.category) p.set('category', m.category);
    if (m.manufacturer) p.set('manufacturer', m.manufacturer);
    if (m.brand) p.set('brand', m.brand);
    if (m.minPrice) p.set('minPrice', m.minPrice);
    if (m.maxPrice) p.set('maxPrice', m.maxPrice);
    const qs = p.toString();
    return qs ? `${basePath}?${qs}` : basePath;
  };

  // Clear all filters but keep the context (query / vehicle).
  const clearedHref = url({ quality: undefined, express: undefined, category: undefined, brand: undefined, minPrice: undefined, maxPrice: undefined, ...(brandKey === 'manufacturer' ? { manufacturer: undefined } : {}) });

  const applyPrice = () => {
    setOpen(false);
    router.push(url({ minPrice: minP.trim() || undefined, maxPrice: maxP.trim() || undefined }));
  };

  const brandValue = current[brandKey];
  const activeCount =
    (showCategories && current.category ? 1 : 0) +
    (brandValue ? 1 : 0) +
    (current.quality ? 1 : 0) +
    (current.express ? 1 : 0) +
    (current.minPrice || current.maxPrice ? 1 : 0);

  const hasFacets = (showCategories && facets.categories.length > 0) || facets.manufacturers.length > 0 || !!facets.price;

  const Section = ({ icon: Icon, title, children }: { icon: typeof Tag; title: string; children: React.ReactNode }) => (
    <div className="py-4 border-b last:border-b-0">
      <p className="flex items-center gap-2 text-sm font-semibold mb-2.5">
        <Icon className="h-4 w-4 text-primary" /> {title}
      </p>
      {children}
    </div>
  );

  const FilterRow = ({ label, count, active, href }: { label: string; count?: number; active: boolean; href: string }) => (
    <Link
      href={href}
      onClick={() => setOpen(false)}
      className={`flex items-center justify-between gap-2 px-2 py-1.5 rounded-md text-sm transition-colors ${
        active ? 'bg-primary/10 text-primary font-medium' : 'hover:bg-accent text-foreground'
      }`}
    >
      <span className="flex items-center gap-1.5 min-w-0">
        {active && <Check className="h-3.5 w-3.5 shrink-0" />}
        <span className="truncate">{label}</span>
      </span>
      {typeof count === 'number' && <span className="text-xs text-muted-foreground shrink-0">{count}</span>}
    </Link>
  );

  const content = (
    <div className="text-sm">
      <div className="flex items-center justify-between pb-3 border-b">
        <span className="font-semibold flex items-center gap-2">
          <SlidersHorizontal className="h-4 w-4 text-primary" />
          {t(locale, 'filters.title')}
        </span>
        {activeCount > 0 && (
          <Link href={clearedHref} onClick={() => setOpen(false)} className="text-xs text-primary hover:underline">
            {t(locale, 'filters.clearAll')}
          </Link>
        )}
      </div>

      {/* Category (search context only) */}
      {showCategories && facets.categories.length > 0 && (
        <Section icon={Tag} title={t(locale, 'filters.category')}>
          <div className="space-y-0.5 max-h-72 overflow-y-auto pr-1">
            {facets.categories.map((c) => {
              const active = current.category === String(c.id);
              return (
                <FilterRow key={c.id} label={c.name} count={c.count} active={active}
                  href={url({ category: active ? undefined : String(c.id) })} />
              );
            })}
          </div>
        </Section>
      )}

      {/* Brand */}
      {facets.manufacturers.length > 0 && (
        <Section icon={Building2} title={t(locale, 'filters.brand')}>
          <div className="space-y-0.5 max-h-72 overflow-y-auto pr-1">
            {facets.manufacturers.map((m) => {
              const active = brandValue === String(m.id);
              return (
                <FilterRow key={m.id} label={m.name} count={m.count} active={active}
                  href={url({ [brandKey]: active ? undefined : String(m.id) })} />
              );
            })}
          </div>
        </Section>
      )}

      {/* Quality */}
      <Section icon={ShieldCheck} title={t(locale, 'filters.quality')}>
        <div className="space-y-0.5">
          <FilterRow label={t(locale, 'searchPage.all')} active={!current.quality} href={url({ quality: undefined })} />
          <FilterRow label="Original (OEM)" active={current.quality === 'original'} href={url({ quality: current.quality === 'original' ? undefined : 'original' })} />
          <FilterRow label={t(locale, 'searchPage.aftermarket')} active={current.quality === 'aftermarket'} href={url({ quality: current.quality === 'aftermarket' ? undefined : 'aftermarket' })} />
        </div>
      </Section>

      {/* Express delivery */}
      <Section icon={Zap} title={t(locale, 'filters.delivery')}>
        <FilterRow label={t(locale, 'searchPage.expressDelivery')} active={current.express === '1'}
          href={url({ express: current.express === '1' ? undefined : '1' })} />
      </Section>

      {/* Price range */}
      {facets.price && (
        <Section icon={Wallet} title={t(locale, 'filters.priceRange')}>
          <div className="flex items-center gap-2">
            <input type="number" inputMode="numeric" min={0} value={minP}
              onChange={(e) => setMinP(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') applyPrice(); }}
              placeholder={String(Math.round(facets.price.min))}
              className="w-full min-w-0 px-2 py-1.5 text-sm border rounded-md bg-background" aria-label={t(locale, 'filters.minPrice')} />
            <span className="text-muted-foreground">–</span>
            <input type="number" inputMode="numeric" min={0} value={maxP}
              onChange={(e) => setMaxP(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') applyPrice(); }}
              placeholder={String(Math.round(facets.price.max))}
              className="w-full min-w-0 px-2 py-1.5 text-sm border rounded-md bg-background" aria-label={t(locale, 'filters.maxPrice')} />
          </div>
          <button onClick={applyPrice}
            className="mt-2 w-full px-3 py-1.5 text-sm font-medium rounded-md bg-primary text-primary-foreground hover:bg-primary/90 transition-colors">
            {t(locale, 'filters.apply')}
          </button>
        </Section>
      )}
    </div>
  );

  if (!hasFacets) return null;

  return (
    <>
      <aside className="hidden lg:block w-60 shrink-0">
        <div className="sticky top-24 rounded-lg border bg-background p-4">{content}</div>
      </aside>

      {/* Floating filter button — centered above the bottom nav so it's always
          reachable on mobile without scrolling back up through the results. */}
      <button onClick={() => setOpen(true)}
        aria-label={t(locale, 'filters.title')}
        className="lg:hidden fixed bottom-[calc(4.5rem_+_env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 pl-4 pr-4 py-2.5 rounded-full bg-primary text-primary-foreground shadow-lg shadow-black/20 text-sm font-semibold active:scale-95 transition-transform">
        <SlidersHorizontal className="h-4 w-4" />
        {t(locale, 'filters.title')}
        {activeCount > 0 && (
          <span className="h-5 min-w-5 px-1 rounded-full bg-white text-primary text-xs font-bold flex items-center justify-center">{activeCount}</span>
        )}
      </button>

      {open && (
        <div className="lg:hidden fixed inset-0 z-[200]">
          <div className="absolute inset-0 bg-black/40" onClick={() => setOpen(false)} />
          <div className="absolute left-0 top-0 bottom-0 w-[85%] max-w-sm bg-background shadow-xl overflow-y-auto p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="font-semibold">{t(locale, 'filters.title')}</span>
              <button onClick={() => setOpen(false)} className="p-1.5 rounded-md hover:bg-accent" aria-label="Stäng">
                <X className="h-5 w-5" />
              </button>
            </div>
            {content}
          </div>
        </div>
      )}
    </>
  );
}
